import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { CalendarIcon, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { cn } from "../utils";
import useTranslation from "../hooks/useTranslation";
import {
  type DateRange,
  type DateRangePreset,
  normalizeCustomRange,
  presetToDateRange,
} from "../utils/dateRange";

import "react-day-picker/style.css";

export type { DateRange, DateRangePreset };

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
  /** If provided, show a star next to each preset — filled when preset === pinnedPreset */
  pinnedPreset?: DateRangePreset | null;
  onPinPreset?: (preset: DateRangePreset) => void;
}

const PRESETS: DateRangePreset[] = [
  "today",
  "this_week",
  "this_month",
  "last_30_days",
  "last_month",
  "this_quarter",
  "ytd",
  "all_time",
];

export function DateRangePicker({ value, onChange, className, pinnedPreset, onPinPreset }: DateRangePickerProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(
    () => (!value.from && !value.to ? "all_time" : "custom")
  );

  const presetLabels: Record<DateRangePreset, string> = {
    today: t.dateRangePicker.today,
    this_week: t.dateRangePicker.thisWeek,
    this_month: t.dateRangePicker.thisMonth,
    last_30_days: t.dateRangePicker.last30Days,
    last_month: t.dateRangePicker.lastMonth,
    this_quarter: t.dateRangePicker.thisQuarter,
    ytd: t.dateRangePicker.ytd,
    all_time: t.dateRangePicker.allTime,
    custom: t.dateRangePicker.custom,
  };

  function handlePreset(preset: DateRangePreset) {
    setSelectedPreset(preset);
    onChange(presetToDateRange(preset));
    setOpen(false);
  }

  function handleCalendarSelect(range: { from?: Date; to?: Date } | undefined) {
    setSelectedPreset("custom");
    // Borner ici, a la source : tous les consommateurs (journal, sessions,
    // prop firm, discipline, dashboard) passent la plage telle quelle a leurs
    // requetes. Sans cela, un jour unique donne une fenetre de largeur nulle.
    const next = normalizeCustomRange(range);
    onChange(next);
    if (next.from && next.to) setOpen(false);
  }

  function triggerLabel() {
    if (!value.from && !value.to) return t.dateRangePicker.allTime;
    if (value.from && !value.to) return format(value.from, "MMM d, yyyy");
    if (value.from && value.to)
      return `${format(value.from, "MMM d, yyyy")} – ${format(value.to, "MMM d, yyyy")}`;
    // unreachable: from=null with to!=null is not a valid react-day-picker state
    return t.dateRangePicker.allTime;
  }

  const current = selectedPreset;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("justify-start text-left font-normal gap-2", className)}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
          <span className="font-mono text-sm">{triggerLabel()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Preset list */}
          <div className="flex flex-col border-r py-2 min-w-[160px]">
            {PRESETS.map((preset) => {
              const isPinned = onPinPreset && pinnedPreset === preset;
              return (
                <div key={preset} className="flex items-center group/preset">
                  <button
                    onClick={() => handlePreset(preset)}
                    className={cn(
                      "flex-1 px-4 py-1.5 text-sm text-left hover:bg-muted transition-colors",
                      current === preset && "font-semibold text-primary"
                    )}
                  >
                    {presetLabels[preset]}
                  </button>
                  {onPinPreset && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onPinPreset(preset); }}
                      className={cn(
                        "mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors",
                        "opacity-0 group-hover/preset:opacity-100",
                        isPinned ? "opacity-100 text-yellow-400 hover:text-yellow-500" : "text-muted-foreground hover:text-yellow-400"
                      )}
                      title={t.pages.pinAsDefault}
                    >
                      <Star className={cn("h-3 w-3", isPinned && "fill-yellow-400")} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Double calendar */}
          <div className="drp-calendar">
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={
                value.from || value.to
                  ? { from: value.from ?? undefined, to: value.to ?? undefined }
                  : undefined
              }
              onSelect={handleCalendarSelect}
              className="p-3"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
